/**
 * iOS 홈 화면 앱(standalone PWA) 뷰포트 높이 오차 보정 — `--vp-fix` CSS 변수.
 *
 * 증상
 *  - `apple-mobile-web-app-status-bar-style=black-translucent` + `viewport-fit=cover` 조합에서
 *    iOS 가 가끔(특히 앱을 켠 직후) 레이아웃 뷰포트 높이를 "화면 − 상태바" 로 계산한다.
 *    콘텐츠는 상태바 밑까지 올라가 그려지므로 그만큼 화면 하단이 비고,
 *    position:fixed 인 탭바·FAB·바텀시트가 그 위에 떠 보인다(WebKit 알려진 버그).
 *
 * 보정
 *  - standalone + iOS + 세로 방향에서 `screen.height − window.innerHeight` 를 재서
 *    상태바 높이 범위(0 < gap ≤ 80px) 이면 `--vp-fix: <gap>px` 로 둔다. 정상이면 0px.
 *  - CSS 쪽은 앱 셸 높이 `calc(100dvh + var(--vp-fix))`, 하단 고정 요소는 `bottom` 에서 빼서
 *    실제 화면 바닥에 맞춘다(components.css 의 `--vp-fix` 사용처 참고).
 *  - 브라우저 모드(Safari/Chrome 탭)·안드로이드·가로 방향에서는 항상 0px.
 *  - 상태바가 불투명(index.html `default`, 2026-10-22~)이면 콘텐츠가 상태바 아래에서 시작해
 *    "화면 − 상태바" 가 정상 높이다 → 보정하지 않는다(0px). black-translucent 로 되돌리면 다시 동작.
 *
 * 키보드가 열려 innerHeight 가 크게 줄면(gap > 80) 값을 건드리지 않고 이전 값을 유지한다.
 */
const VAR = "--vp-fix";
const MAX_GAP = 80;

function isIosStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  if (!("standalone" in nav)) return false; // iOS 전용 속성 — 안드로이드 제외
  return nav.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
}

function isTranslucentStatusBar(): boolean {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-status-bar-style"]');
  return meta?.content === "black-translucent";
}

function measure() {
  const root = document.documentElement;
  if (!isIosStandalone() || !isTranslucentStatusBar() || window.innerWidth > window.innerHeight) {
    root.style.setProperty(VAR, "0px");
    return;
  }
  const gap = Math.round(window.screen.height - window.innerHeight);
  if (gap > MAX_GAP) return; // 키보드 등 — 판단 보류
  root.style.setProperty(VAR, `${Math.max(gap, 0)}px`);
}

let installed = false;
export function installIosViewportFix() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  measure();
  // iOS 가 켜진 직후 몇 프레임 뒤에 뷰포트를 다시 잡는 경우가 있어 지연 재측정
  [100, 400, 1200].forEach((ms) => window.setTimeout(measure, ms));
  window.addEventListener("resize", measure);
  window.addEventListener("orientationchange", () => window.setTimeout(measure, 150));
  window.addEventListener("pageshow", measure);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") { measure(); window.setTimeout(measure, 300); }
  });
  window.visualViewport?.addEventListener("resize", measure);
}
